import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { tap, catchError, switchMap, filter, take } from 'rxjs/operators';
import { NavigationStart, Router } from '@angular/router';
import { AppToastrService } from '../services/toastr/app-toastr.service';
import { SpNetworkService } from '../services/network-service/sp-network.service';
import { LoginService } from '@app/registration/service/login.service';
import { ApiCallTrackingEvent, TrackingInput } from '../models/tracking-event.model';
import { InstrumentationService } from '../services/instrumentation/instrumentation.service';
import { environment } from '@env/environment';

@Injectable()
export class SellerPortalInterceptor implements HttpInterceptor {

  public isOnline: boolean = true;
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  private trackedRequests = new Map<string, boolean>();

  constructor(
    private router: Router,
    private toastrService: AppToastrService,
    private spNetworkService: SpNetworkService,
    private loginService: LoginService,
    private instrumentationService: InstrumentationService
  ) {
    this.spNetworkService.createOnline$().subscribe(isOnline => {
      this.isOnline = isOnline;
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.trackedRequests.clear();
        console.log('Navigation detected → duplicate map cleared');
      }
    });

  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    var cloneReq;
    const auth_token: string = 'Bearer ' + sessionStorage.getItem('accessToken') || '';
    const excludeUrl = environment.trackingApiUrl;
    //const orgId: string = sessionStorage.getItem('orgId') || '';
    //const mobile_number = null
    if (request.method == 'GET') {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': auth_token
      })
      cloneReq = request.clone({ headers });
    } else if (request.method == 'POST') {
      if (request.url.includes(excludeUrl)) {
        cloneReq = request.clone();
      }
      else{
        const headers = new HttpHeaders({
          'Authorization': auth_token
        });
        cloneReq = request.clone({ headers });
      }
    } else if (request.method == 'PUT') {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': auth_token
      });
      cloneReq = request.clone({ headers });
    } else if (request.method == 'DELETE') {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': auth_token
      });
      cloneReq = request.clone({ headers });
    }
    if (this.isOnline) {
      return next.handle(cloneReq).pipe(
        tap((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            // response handling
            const requestKey = this.getRequestKey(request);
            //All API tracking event
            const trackingInput: TrackingInput<ApiCallTrackingEvent> = {
                eventLabel: '',
                eventName: 'API_CALL',
                eventSource: '',
                url: request.url,
                method: request.method,
                headers: this.extractHeaders(request),
                body: request.body,
                statusCode: event.status,
                statusText: event.statusText,
                responseBody: event.body,
            };
            if (!this.trackedRequests.has(requestKey) && !request.url.includes(excludeUrl)) {
               this.trackedRequests.set(requestKey, true);
               this.logAPICall(trackingInput);
            }
          }
        }),
        catchError(err => {
          //All API tracking event
            const trackingInput: TrackingInput<ApiCallTrackingEvent> = {
              eventLabel: '',
              eventName: 'API_CALL',
              eventSource: '',
              url: request.url,
              method: request.method,
              headers: this.extractHeaders(request),
              body: request.body,
              statusCode: err.status,
              statusText: err.statusText,
              responseBody: err.error,
          };
          if (!request.url.includes(excludeUrl)) {
              this.logAPICall(trackingInput);
          }
          if (err instanceof HttpErrorResponse) {
            if (err.status === 403 || err.status === 401) {
              // localStorage.clear();
              // sessionStorage.clear();
              // this.toastrService.error('Your session has expired , please login again to continue');
              // window.location.href = "/registration/login";
              return this.handle401Error(cloneReq, next);
            } else {
              if (err?.url.includes('/seller/api/v1/e-invoice') && err?.url.includes('eInvoiceFormat=JSON') && err?.status === 400) {
                this.toastrService.error('No report found for the date you selected');
              } else if (err?.url.includes('/seller/api/v1/e-invoice') && err?.url.includes('eInvoiceFormat=EXCEL') && err?.status === 400) {
                this.toastrService.error('No report found for the date you selected');
              }
              if(err?.url.includes('seller/api/v1/campaign/download-products') && err?.status === 400){
                this.toastrService.error(err?.error?.errorMessage || 'No eligible products found for this campaign');
              }
              else if (
                err?.url.includes(
                    'seller/catalog-upload'
                ) &&
                err?.status === 400
              ) {
                this.toastrService.error(
                  err?.error?.error?.message ||
                        'Please upload valid image'
                );
              }
              else {
                this.toastrService.error(err.error.errorMessage ? err.error.errorMessage : err.error.error.errorMessage ? err.error.error.errorMessage : err.error.error.message ? err.error.error.message :  'Something Went Wrong!');
              }
            }
          }
          return throwError(err);
        })
      );
    } else {
      this.toastrService.error('There is no internet connection, please try again');
    }
  }

  private extractHeaders(req: HttpRequest<any>): Record<string, string> {
    const headers: Record<string, string> = {};
    req.headers.keys().forEach(key => {
      headers[key] = req.headers.get(key) as string;
    });
    return headers;
  }

  private normalizeHeaders(headers: any): Record<string, string> {
    const headerObj: Record<string, string> = {};
    headers.keys().sort().forEach(key => {
      headerObj[key.toLowerCase()] = headers.get(key) || '';
    });
    return headerObj;
  }

  private getRequestKey(req: HttpRequest<any>): string {
    const { method, urlWithParams, body, headers } = req;

    return JSON.stringify({
      method,
      url: urlWithParams,
      body: body ? JSON.stringify(body) : null,
      headers: this.normalizeHeaders(headers),
    });
  }


  private logAPICall(trackingInput: TrackingInput<ApiCallTrackingEvent>){
    this.instrumentationService.logEvent<ApiCallTrackingEvent>(
        trackingInput
    );
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.loginService.refreshToken().pipe(
        switchMap((response: any) => {
          this.isRefreshing = false;
          if (response.data.accessToken !== null) {
            this.refreshTokenSubject.next(response.data.accessToken);
            return next.handle(
              this.addToken(request, response.data.accessToken)
            );
          } else {
            localStorage.clear();
            sessionStorage.clear();
            localStorage.setItem('removeSessionStorage', 'true');
            localStorage.removeItem('removeSessionStorage')
            this.toastrService.error('Invalid Refresh Token , please login again to continue');
            window.location.href = "/registration/login";
          }
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(accessToken => {
          return next.handle(this.addToken(request, accessToken));
        })
      );
    }
  }
}
